import {
  ActionsGroup,
  Box,
  Button,
  DialogCard,
  DialogFooterV2,
  ExternalLink,
  IconButton,
  Icons,
  makeDialog,
  Stack,
  tokens,
  Typography,
  useCloseDialog,
} from '@cast/design-system';

import { EXTERNAL_LINKS } from '@cast/constants';

import redesignPreview from '../_assets/redesign-preview.png';

export type RedesignAnnouncementDialogProps = {
  oneClickUrl: string;
};

const RedesignAnnouncementDialogContent = ({
  oneClickUrl,
}: RedesignAnnouncementDialogProps) => {
  const closeDialog = useCloseDialog();

  return (
    <DialogCard
      size={600}
      hideCloseButton
      testId="console-redesign-announcement-dialog"
    >
      <Box
        position="relative"
        sx={{
          borderRadius: '4px 4px 0 0',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          background: `radial-gradient(
            ellipse 34.56% 169.3% at 19.6% 50%,
            ${tokens.theme.gradient.marketingBannerStop1} 0%,
            ${tokens.theme.gradient.marketingBannerStop2} 50%,
            ${tokens.theme.gradient.marketingBannerStop3} 100%
          )`,
        }}
      >
        <Box
          component="img"
          src={redesignPreview}
          alt="Redesigned console preview"
          sx={{
            display: 'block',
            width: '100%',
            borderRadius: '4px 4px 0 0',
          }}
        />
        <IconButton
          variant="ghost"
          size="sm"
          onClick={() => closeDialog()}
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: tokens.theme.fg.structural.primaryOnInverse,
          }}
          testId="console-redesign-announcement-dialog-close"
        >
          <Icons.X size={16} />
        </IconButton>
      </Box>

      <Stack gap={8} px={16} pt={16} pb={12}>
        <Typography variant="h5">
          We’ve rebuilt the console around your feedback
        </Typography>
        <Typography variant="P14R">
          A redesigned console, with long-awaited additions like universal
          search and dark mode, so you can work more quickly and more
          comfortably.
        </Typography>
        <ExternalLink
          link={EXTERNAL_LINKS.castai_new_console_experience}
          sx={{ typography: 'P14M' }}
        >
          Full story on our blog from the design team that built it
        </ExternalLink>
      </Stack>

      <DialogFooterV2
        divider
        endContent={
          <ActionsGroup>
            <Button
              variant="primary"
              size="md"
              component="a"
              href={oneClickUrl}
              target="_blank"
              rel="noopener noreferrer"
              testId="console-redesign-announcement-dialog-cta"
            >
              Take a look around
            </Button>
          </ActionsGroup>
        }
      />
    </DialogCard>
  );
};

export const useRedesignAnnouncementDialog =
  makeDialog<RedesignAnnouncementDialogProps>(
    RedesignAnnouncementDialogContent,
    { closeOnOverlayClick: true }
  );
